import React, { useCallback, useEffect, useState } from "react";
import { Modal, Table, Spin, notification } from "antd";
import { FiClock } from "react-icons/fi";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { getCredentialEditHistory } from "../../../../service/credentials.service";
import { ChangeLine, EmptyState, HistoryHeader } from "./styles";

const PAGE_SIZE = 20;

// Mapa apiKey -> id de tradução do rótulo do campo.
const FIELD_LABELS = {
  credentialName: "credential_name",
  companyName: "company_name_credential",
  jobTitle: "assignment",
};

/**
 * Histórico de correções/reimpressões da credencial (auditoria) — INFORMACP-4388.
 * Consulta paginada do endpoint edit-history (de/para, motivo, atendente, data, canal).
 */
export function CredentialEditHistoryModal({ open, setOpen, credential }) {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const credentialId = credential?.id;
  const eventId = localStorage.getItem("serviceEventId");
  const opportunityId = localStorage.getItem("serviceOpportunityId");

  const fetchHistory = useCallback(
    async (pageToLoad) => {
      if (!credentialId) return;
      setLoading(true);
      try {
        const response = await getCredentialEditHistory({
          token,
          credentialId,
          eventId,
          opportunityId,
          page: pageToLoad,
          size: PAGE_SIZE,
        });
        setContent(response.data?.content || []);
        setTotalElements(response.data?.totalElements || 0);
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description:
            error.response?.data?.description ||
            intl.formatMessage({
              id: "api.error." + (error.response?.data?.code || "unknown"),
            }),
        });
        setContent([]);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [credentialId, token, eventId, opportunityId, intl]
  );

  useEffect(() => {
    if (open) {
      setPage(0);
      fetchHistory(0);
    }
  }, [open, fetchHistory]);

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  const renderChanges = (changes) => {
    const entries = Object.entries(changes || {});
    if (entries.length === 0) return "-";
    return entries.map(([field, diff]) => (
      <ChangeLine key={field}>
        <span className="field">
          {intl.formatMessage({ id: FIELD_LABELS[field] || field })}:
        </span>{" "}
        <span className="before">{diff?.before || "-"}</span>{" "}
        <span aria-hidden>→</span>{" "}
        <span className="after">{diff?.after || "-"}</span>
      </ChangeLine>
    ));
  };

  const columns = [
    {
      title: intl.formatMessage({ id: "changed_at" }),
      dataIndex: "printedAt",
      key: "printedAt",
      width: "16%",
      render: formatDate,
    },
    {
      title: intl.formatMessage({ id: "changes" }),
      dataIndex: "changes",
      key: "changes",
      render: renderChanges,
    },
    {
      title: intl.formatMessage({ id: "reason" }),
      dataIndex: "reason",
      key: "reason",
      render: (value) => value || "-",
    },
    {
      title: intl.formatMessage({ id: "changed_by" }),
      dataIndex: "performedByEmail",
      key: "performedByEmail",
      render: (value) => value || "-",
    },
    {
      title: intl.formatMessage({ id: "channel" }),
      dataIndex: "channel",
      key: "channel",
      width: "10%",
      render: (value) => value || "-",
    },
  ];

  return (
    <Modal
      visible={open}
      onCancel={() => setOpen(false)}
      footer={null}
      width={860}
      centered
      title={null}
    >
      <HistoryHeader>
        <FiClock style={{ fontSize: "18px", color: "rgba(6, 142, 195, 1)" }} />
        <span className="title">
          {intl.formatMessage({ id: "edit_reprint_history_title" })}
        </span>
      </HistoryHeader>

      {loading ? (
        <EmptyState>
          <Spin />
        </EmptyState>
      ) : content.length === 0 ? (
        <EmptyState>
          {intl.formatMessage({ id: "no_change_history" })}
        </EmptyState>
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={content}
          size="small"
          pagination={{
            current: page + 1,
            pageSize: PAGE_SIZE,
            total: totalElements,
            showSizeChanger: false,
            onChange: (nextPage) => {
              const zeroBased = nextPage - 1;
              setPage(zeroBased);
              fetchHistory(zeroBased);
            },
          }}
        />
      )}
    </Modal>
  );
}

export default CredentialEditHistoryModal;
